// =============================================================================
// DraggableAppointmentBlock - Blocco appuntamento trascinabile con tooltip
// =============================================================================

import { useState, useRef } from 'react';
import { parseISO, differenceInMinutes } from 'date-fns';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Users } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { getServiceColor, getContrastTextColor, lightenColor } from '../utils/color.utils';
import { getAppointmentStyle } from '../utils/grid.utils';
import { useTenantTimeZone } from '../../../store/tenantStore';
import { civilDateInTz, minutesOfDayInTz, timeStringInTz } from '../../../lib/tenantTime';
import { DEFAULT_GRID_START_HOUR } from '../utils/grid.constants';
import { AppointmentTooltip } from './AppointmentTooltip';
import { useJoinTargetAppointmentId } from '../../../store/schedulerStore';
import type { AppointmentBlockProps } from '../types/scheduler.types';

export function DraggableAppointmentBlock({ appointment, resourceId, isDragOverlay, onAppointmentClick, bufferBeforeMinutes = 0, bufferAfterMinutes = 0, showBufferZones = false, gridStartHour = DEFAULT_GRID_START_HOUR, gridEndHour, totalGridMinutes = 720 }: AppointmentBlockProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);
  // Evidenzia l'evento a cui l'utente sta scegliendo di aggregare il cliente
  const joinTargetAppointmentId = useJoinTargetAppointmentId();
  const isJoinTarget = joinTargetAppointmentId === appointment.id;
  const start = parseISO(appointment.scheduledStart);
  const end = parseISO(appointment.scheduledEnd);
  const durationMinutes = differenceInMinutes(end, start);
  
  const draggableId = `${appointment.id}__${resourceId}`;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
    node,
  } = useDraggable({
    id: draggableId,
    data: {
      appointment,
      appointmentId: appointment.id,
      resourceId,
      duration: durationMinutes,
    },
  });
  
  if (node.current) {
    node.current.dataset.appointmentId = appointment.id;
  }
  
  const timeZone = useTenantTimeZone();
  const civilDay = civilDateInTz(start, timeZone);

  const positionStyle = getAppointmentStyle(
    appointment.scheduledStart,
    appointment.scheduledEnd,
    gridStartHour,
    totalGridMinutes,
    civilDay,
    timeZone
  );
  
  // Controlla se l'appuntamento è nelle ore visibili: le ore della griglia sono
  // orari di parete dell'organizzazione, quindi anche questi vanno letti li'.
  const aptStartHour = minutesOfDayInTz(start, timeZone) / 60;
  const aptEndHour = minutesOfDayInTz(end, timeZone) / 60;
  if (aptEndHour <= gridStartHour || (gridEndHour !== undefined && aptStartHour >= gridEndHour)) return null;
  
  const serviceColor = getServiceColor(appointment.serviceDefinitionId, appointment.serviceColor);
  const bgColor = lightenColor(serviceColor, 0.75);
  const textColor = getContrastTextColor(bgColor);
  const borderColor = serviceColor;
  
  const displayName = appointment.customerName 
    ? appointment.customerName.split(' ').slice(-1)[0] 
    : '(Interno)';
  
  const hasBufferBefore = showBufferZones && bufferBeforeMinutes > 0 && !isDragging;
  const hasBufferAfter = showBufferZones && bufferAfterMinutes > 0 && !isDragging;
  const bufferBeforeWidthPct = durationMinutes > 0 ? (bufferBeforeMinutes / durationMinutes) * 100 : 0;
  const bufferAfterWidthPct = durationMinutes > 0 ? (bufferAfterMinutes / durationMinutes) * 100 : 0;
  
  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        (blockRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }}
      data-appointment-id={appointment.id}
      className={cn(
        'absolute top-1 bottom-1',
        isDragging ? 'z-50' : 'z-10',
      )}
      style={{
        ...positionStyle,
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
        overflow: 'visible',
      }}
      onMouseEnter={() => !isDragging && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onAppointmentClick?.(appointment.id);
      }}
      {...attributes}
      {...listeners}
    >
      {hasBufferBefore && (
        <div
          className="absolute top-0 bottom-0 rounded-l-md pointer-events-none"
          style={{
            right: '100%',
            width: `${bufferBeforeWidthPct}%`,
            backgroundColor: serviceColor,
            opacity: 0.2,
            backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 6px)',
          }}
          title={`Buffer prima: ${bufferBeforeMinutes} min`}
        />
      )}
      
      <div
        className={cn(
          'w-full h-full rounded-md px-2 py-1 overflow-hidden',
          'border-l-4 shadow-sm transition-all group',
          isDragging ? 'shadow-xl cursor-grabbing' : 'hover:shadow-md cursor-grab',
          isDragOverlay && 'shadow-2xl ring-2 ring-blue-500',
          isJoinTarget && 'ring-2 ring-offset-1 ring-indigo-500 shadow-lg animate-pulse-slow',
        )}
        style={{ backgroundColor: bgColor, borderLeftColor: borderColor }}
      >
        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-50 transition-opacity">
          <GripVertical className="w-3 h-3" style={{ color: textColor }} />
        </div>

        {/* Badge occupazione eventi di gruppo (x/max) */}
        {(appointment.maxClients ?? 1) > 1 && (
          <div
            className="absolute bottom-0.5 right-1 flex items-center gap-0.5 text-[10px] font-semibold opacity-90"
            style={{ color: textColor }}
          >
            <Users className="w-3 h-3" />
            {appointment.participantCount ?? 1}/{appointment.maxClients}
          </div>
        )}

        <div className="text-xs font-semibold truncate pr-4" style={{ color: textColor }}>
          {timeStringInTz(start, timeZone)} - {displayName}
        </div>
        <div className="text-xs truncate opacity-80" style={{ color: textColor }}>
          {appointment.serviceName}
        </div>
      </div>
      
      {hasBufferAfter && (
        <div
          className="absolute top-0 bottom-0 rounded-r-md pointer-events-none"
          style={{
            left: '100%',
            width: `${bufferAfterWidthPct}%`,
            backgroundColor: serviceColor,
            opacity: 0.2,
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 6px)',
          }}
          title={`Buffer dopo: ${bufferAfterMinutes} min`}
        />
      )}
      
      <AppointmentTooltip 
        appointment={appointment} 
        visible={showTooltip && !isDragging}
        bufferBeforeMinutes={bufferBeforeMinutes}
        bufferAfterMinutes={bufferAfterMinutes}
        anchorRef={blockRef}
      />
    </div>
  );
}
